import { List, OrderedSet } from 'immutable';
import React, { memo, useContext } from 'react';
import { useReframe } from 'nike-re-framejs';

import { firstToUpperCase, formatDate } from '@/common/client';
import { EventCashinOffers } from '@/cashins/views/CashinOffers';
import withSportHeader from '@/common/components/sportWrapper';
import BoxOptionsContext from '../context/BoxOptionsContext';
import { defaultComparator } from '@/common/util';
import immutableMemo from '@/common/immutableMemo';
import MiniScoreboardPrematch from './MiniScoreboardPrematch';
import Tips from './Tips';
import MarketSlider from './MarketSlider';
import { trackingPropsForSportEvent } from '@/common/tracking';
import { nike1SportEvent$ } from '@/nikeone/subs';
import { nike1PrematchEventOptionsInfo$ } from '@/matchOptions/subs';
import BoxIdContext from 'nike-ui/src/common/BoxIdContext';
import { CashinPosition } from 'nike-ui/src/components/turboLive/components/cashin/constants';
import CashinPositionContext from 'nike-ui/src/common/CashinPositionContext';

const PrematchSection = memo(function PrematchSection({ boxStore, boxId, shortcutMarkets }) {
    const hierarchy = <PrematchHierarchy boxStore={boxStore} boxId={boxId} />;
    if (shortcutMarkets) {
        return (
            <MarketSlider boxStore={boxStore} boxId={boxId}>
                {hierarchy}
            </MarketSlider>
        );
    }
    return hierarchy;
});

export default PrematchSection;

function Match({ useRaceTemplate, sportEvent, boxStore = 'filteredBoxStore' }) {
    const { editorTips } = useContext(BoxOptionsContext);
    const eventId = sportEvent.get('sportEventId');
    const { derefSub } = useReframe();
    const fullSportEvent = derefSub(nike1SportEvent$(boxStore, eventId));

    if (!fullSportEvent) {
        return null;
    }
    const firstBetId = sportEvent.getIn(['betIds', 0]);
    const info = derefSub(nike1PrematchEventOptionsInfo$(boxStore, eventId, firstBetId));

    return (
        <div
            data-atid="nikeone-match"
            className="nikeone-match nikeone-match-prematch"
            {...trackingPropsForSportEvent(fullSportEvent)}
        >
            <MiniScoreboardPrematch sportEvent={fullSportEvent} betId={firstBetId} boxStore={boxStore} info={info} />
            {sportEvent.get('betIds').map(id => (
                <Tips
                    boxStore={boxStore}
                    useRaceTemplate={useRaceTemplate}
                    key={id}
                    betId={id}
                    showEditorTips={editorTips}
                />
            ))}
            <EventCashinOffers eventId={eventId} />
        </div>
    );
}

const MatchList = memo(function MatchList({ sportEvents, boxStore }) {
    const { derefSub, subscribe } = useReframe();
    const betGroup = sportEvents.get(0);
    if (!betGroup) {
        return null;
    }
    const isPrimaryMarket = derefSub(['nikeone/market', boxStore, betGroup.get('marketId')]).get('primary');
    const { matchTemplate } = subscribe(nike1SportEvent$(boxStore, betGroup.get('sportEventId')));
    const useRaceTemplate = matchTemplate === 'RACE' && isPrimaryMarket;
    const groupSportEvents = useRaceTemplate ? sportEvents.filter(sportEvent => sportEvent.get('first')) : sportEvents;
    return (
        <div className="top-bets-box">
            {groupSportEvents.map(sportEvent => (
                <Match
                    useRaceTemplate={useRaceTemplate}
                    sportEvent={sportEvent}
                    boxStore={boxStore}
                    key={sportEvent.get('sportEventId') + sportEvent.getIn(['betIds', 0])}
                />
            ))}
        </div>
    );
});

function ConditionalMarket({ betHeader, activeMarket }) {
    const market = (activeMarket || '').toUpperCase();
    const inherited =
        betHeader.get('headerDetail').toUpperCase() === market || betHeader.get('header').toUpperCase() === market;
    if (inherited) {
        return null;
    }
    return <span className="group-label-market">{firstToUpperCase(betHeader.get('headerDetail'))}</span>;
}

const MatchGroup = immutableMemo(function MatchGroup({ boxStore, date, betHeader, activeMarket, sportId }) {
    return (
        <div className="match-group">
            {sportId && <span className={`sport-br bg-${sportId}`} />}
            <div className="group-label">
                <span className="group-label-date">{formatDate(date)}</span>
                <ConditionalMarket betHeader={betHeader} activeMarket={activeMarket} />
            </div>
            <MatchList boxStore={boxStore} sportEvents={betHeader.get('sportEvents')} />
        </div>
    );
});

export const PrematchHierarchy = memo(function PrematchHierarchy({ boxStore, boxId, activeShortcutMarketId }) {
    const derefSub = useReframe();
    const prematchHierarchy = derefSub(['nikeone/box/prematchHierarchy', boxStore, boxId]);
    const activeMarket = activeShortcutMarketId && derefSub(['nikeone/market', boxStore, activeShortcutMarketId]);
    return prematchHierarchy.flatten(1).map(data => {
        return data.get('betHeaders').map(betHeader => (
            <BoxIdContext.Provider value={boxId} key={boxId}>
                <MatchGroup
                    boxStore={boxStore}
                    date={data.get('date')}
                    betHeader={betHeader}
                    activeMarket={activeMarket && activeMarket.get('name')}
                    key={data.get('date') + betHeader.get('header')}
                />
            </BoxIdContext.Provider>
        ));
    });
});

// cela tato zlozitost existuje, lebo chceme bety grupovat podla sportov
export const SuperkurzyHierarchy = memo(function SuperkurzyHierarchy({ boxStore, boxId }) {
    const derefSub = useReframe();
    const prematchHierarchy = derefSub(['nikeone/box/prematchHierarchy', boxStore, boxId]);
    const allSportEvents = derefSub(['nikeone/sportEvents', boxStore]);
    let sports = new OrderedSet();
    let sportNames = {};
    let sportMatches = {};
    const dateHierarchy = prematchHierarchy.flatten(1);
    // tu sa kolektnu vsetky sporty z hierarchie (a nazvy sportov)
    dateHierarchy.forEach(data => {
        data.get('betHeaders').forEach(header => {
            header.get('sportEvents').forEach(sportEvent => {
                const sportEventId = sportEvent.get('sportEventId');
                const sportId = allSportEvents.getIn([sportEventId, 'sportId']);
                if (sportId) {
                    sports = sports.add(sportId);
                    sportNames[sportId] = allSportEvents.getIn([sportEventId, 'sportName']);
                    sportMatches[sportEventId] = sportId;
                }
            });
        });
    });

    function filterSportEvents(betHeader, sportId) {
        return betHeader.update('sportEvents', (sportEvents = List()) =>
            sportEvents.filter(sportEvent => sportMatches[sportEvent.get('sportEventId')] === sportId)
        );
    }

    // cela hierarchia sa renderuje tolkokrat, kolko existuje sportov, v kazdej (sportovej) iteracii sa vyfiltruju
    // sportEventy patriace len danemu sportu
    return (
        <BoxIdContext.Provider value={boxId}>
            <CashinPositionContext.Provider value={{ cashinPosition: CashinPosition.PREMATCH_OVERVIEW }}>
                {sports.map(sportId =>
                    withSportHeader(
                        sportId,
                        sportNames[sportId],
                        dateHierarchy.map(data =>
                            data.get('betHeaders').map(betHeader => {
                                const filtered = filterSportEvents(betHeader, sportId);
                                return filtered.get('sportEvents').size ? (
                                    <MatchGroup
                                        boxStore={boxStore}
                                        date={data.get('date')}
                                        betHeader={filtered}
                                        sportId={sportId}
                                        key={data.get('date') + betHeader.get('header')}
                                    />
                                ) : null;
                            })
                        )
                    )
                )}
            </CashinPositionContext.Provider>
        </BoxIdContext.Provider>
    );
});

export const HomepageHierarchy = memo(function HomepageHierarchy({ boxStore, boxId, limit }) {
    const { derefSub } = useReframe();
    const allSportEvents = derefSub(['nikeone/sportEvents', boxStore]);
    const prematchHierarchy = derefSub(['nikeone/box/prematchHierarchy', boxStore, boxId]);
    const sportEvents = prematchHierarchy
        .flatten(1)
        .flatMap(data => data.get('betHeaders'))
        .flatMap(betHeader => betHeader.get('sportEvents'))
        .sort((a, b) =>
            defaultComparator(
                allSportEvents.getIn([a.get('sportEventId'), 'expiration']),
                allSportEvents.getIn([b.get('sportEventId'), 'expiration'])
            )
        )
        .slice(0, limit ? limit : Number.MAX_VALUE);
    return (
        <BoxIdContext.Provider value={boxId} key={boxId}>
            <CashinPositionContext.Provider value={{ cashinPosition: CashinPosition.PREMATCH_OVERVIEW }} key={boxId}>
                <MatchList boxStore={boxStore} key={boxId} sportEvents={sportEvents} />
            </CashinPositionContext.Provider>
        </BoxIdContext.Provider>
    );
});
